import React from 'react';
import { ArrowLeft, Plus, RefreshCw, Trash2, HelpCircle, Mail, MenuIcon } from 'lucide-react';
import { Country } from '../types/todo';

interface TodoListHeaderProps {
  country: Country;
  onBack: () => void;
  isPreview: boolean;
  onAddList?: () => void;
  onDeleteList?: () => void;
  onShowGuide: () => void;
  onShowResetConfirmation: () => void;
  onAddTask: () => void;
  showCompleted: boolean;
  onToggleShowCompleted: () => void;
  onSendEmail?: () => void;
}

export function TodoListHeader({
  country,
  onBack,
  isPreview,
  onAddList,
  onDeleteList,
  onShowGuide,
  onShowResetConfirmation,
  onAddTask,
  showCompleted,
  onToggleShowCompleted,
  onSendEmail
}: TodoListHeaderProps) {
  const [isSendingEmail, setIsSendingEmail] = React.useState(false);
  const [emailCooldown, setEmailCooldown] = React.useState(0);
  const [showDropdownMenu, setShowDropdownMenu] = React.useState(false);
  const cooldownRef = React.useRef<NodeJS.Timeout>();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Clear interval on unmount
  React.useEffect(() => {
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowDropdownMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSendEmail = async () => {
    if (!onSendEmail || isSendingEmail || emailCooldown > 0) return;
    
    setIsSendingEmail(true);
    try {
      await onSendEmail();
      // Start cooldown
      setEmailCooldown(30);
      cooldownRef.current = setInterval(() => {
        setEmailCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <div className="fixed top-16 left-0 right-0 z-40 bg-transparent">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="flex-shrink-0 p-2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="min-w-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-xl shadow-sm">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate">
                PVT {country.name}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-3">
            {isPreview ? (
              <button
                onClick={onAddList}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors backdrop-blur-sm shadow-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Ajouter</span>
              </button>
            ) : (
              <>
                {/* Desktop Actions */}
                <div className="hidden sm:flex items-center gap-3">
                  <button
                    onClick={onShowGuide}
                    className="p-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm"
                    title="Voir le guide"
                  >
                    <HelpCircle className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button
                    onClick={onShowResetConfirmation}
                    className="p-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm"
                    title="Réinitialiser"
                  >
                    <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={isSendingEmail || emailCooldown > 0}
                    className={`p-2 relative bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm ${
                      emailCooldown > 0
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                    title="Envoyer par email"
                  >
                    {isSendingEmail ? (
                      <div className="w-5 h-5 border-2 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                    {emailCooldown > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-400">
                        {emailCooldown}
                      </div>
                    )}
                  </button>
                  <button
                    onClick={onAddTask}
                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors backdrop-blur-sm shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Tâche</span>
                  </button>
                  <button
                    onClick={onDeleteList}
                    className="p-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm text-red-500 hover:text-red-600"
                    title="Supprimer la liste"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Mobile Dropdown Menu */}
                <div className="sm:hidden relative" ref={menuRef}>
                  <button
                    onClick={() => setShowDropdownMenu(!showDropdownMenu)}
                    className="p-2 bg-white/80 hover:bg-white dark:bg-gray-800/80 dark:hover:bg-gray-700 rounded-full transition-colors backdrop-blur-sm shadow-sm"
                  >
                    <MenuIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  
                  {showDropdownMenu && (
                    <div className="absolute right-0 mt-2 w-56 py-1.5 bg-white dark:bg-gray-800 rounded-xl shadow-premium border border-gray-200 dark:border-gray-700 z-10">
                      <button
                        onClick={() => {
                          onShowGuide();
                          setShowDropdownMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 touch-manipulation"
                      >
                        <HelpCircle className="w-5 h-5 text-gray-500" />
                        <span>Guide</span>
                      </button>
                      <button
                        onClick={() => {
                          onShowResetConfirmation();
                          setShowDropdownMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 touch-manipulation"
                      >
                        <RefreshCw className="w-5 h-5 text-gray-500" />
                        <span>Reinitialiser</span>
                      </button>
                      <button
                        onClick={() => {
                          handleSendEmail();
                          setShowDropdownMenu(false);
                        }}
                        disabled={isSendingEmail || emailCooldown > 0}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      >
                        <Mail className="w-5 h-5 text-gray-500" />
                        <span>Envoyer par email</span>
                      </button>
                      <button
                        onClick={() => {
                          onAddTask();
                          setShowDropdownMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 text-gray-700 dark:text-gray-300 touch-manipulation"
                      >
                        <Plus className="w-5 h-5 text-gray-500" />
                        <span>Ajouter une tache</span>
                      </button>
                      <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                      <button
                        onClick={() => {
                          onDeleteList?.();
                          setShowDropdownMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 active:bg-red-100 dark:text-red-400 touch-manipulation"
                      >
                        <Trash2 className="w-5 h-5" />
                        <span>Supprimer la liste</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}